import {
  ClaimEditField,
  ClaimEditValues,
  MajorCategories,
  MinorCategories,
  UserInfo,
} from 'api/endpoints';
import { AssignedGroup } from 'api/endpoints/assigned-group-users';
import classNames from 'classnames';
import {
  ApNiceSelect,
  EMPTY_OPTION_VALUE,
} from 'components/ApNiceSelect/ApNiceSelect';
import { ReviewersEdit } from 'components/ReviewersEdit/ReviewersEdit';
import { ReviewersBatchItem } from 'components/ReviewersEdit/ReviewersEdit.utils';
import {
  IOption,
  MultiSelectExtendable,
} from 'components/UIComponents/MultiSelectExtendable';
import { useAppDispatch, useAppSelector } from 'hooks/reducerHooks';
import isEqual from 'lodash/isEqual';
import { SectionType } from 'pages/ClaimsContractsToolPage/types';
import { ChangeEvent, useCallback } from 'react';
import { ClaimReviewerData, setClaimReviewers } from 'reducer/claimReviewerReducer';
import { FetchStatus } from 'types/fetch-status.types';
import { formatLegalEntity } from 'utils/format';
import { useResolutionExhibitTypeOptions } from '../../../../../../hooks/useResolutionExhibitTypeOptions';
import { EditFieldFunc } from '../../../../providers/useSingleClaimProvider';
import { SurvivingClaimNoSelect } from '../SurvivingClaimNoSelect/SurvivingClaimNoSelect';
import './CategorySelect.css';
import { MajorMinorCategoryOption } from './MajorMinorCategoryOption';

export enum CategorySelectType {
  Category = 'Category',
  LegalEntities = 'LegalEntities',
  ClaimReviewer = 'ClaimReviewer',
  SurvivingClaimNo = 'SurvivingClaimNo',
  ActionType = 'ActionType',
  ActionSubstatus = 'ActionSubstatus',
  ActionName = 'ActionName',
  ReviewLevel = 'ReviewLevel',
  ActionStatus = 'ActionStatus',
}

const ReviewLevelOptions = [
  { label: 'None', value: 0 },
  { label: 'High', value: 1 },
  { label: 'Medium', value: 2 },
  { label: 'Low', value: 3 },
];

interface CategorySelectProps {
  type: CategorySelectType;
  isMajor: boolean | undefined;
  name: ClaimEditField;
  claim: ClaimEditValues | undefined;
  disabled: boolean | undefined;
  editField: EditFieldFunc;
}

//todo: remove useAppDispatch and all useAppSelector from here
//     useAppDispatch and useAppSelector - cause additional component rerendering side effect
//

export const CategorySelect = ({
  name,
  type,
  isMajor,
  claim,
  disabled,
  editField,
}: CategorySelectProps) => {
  const dispatch = useAppDispatch();
  const activeReviewers = useAppSelector((s) => s.claimReviewer);
  const allUsers = useAppSelector((s) => s.allUsers);
  const allGroups = useAppSelector((s) => s.assignedGroupUsers);
  const selectable = useAppSelector((s) => s.selectable);

  const resolutionOptions = useResolutionExhibitTypeOptions();

  const getActiveUserReviewers = (activeUserIds: number[], users: UserInfo[]) =>
    users.map((u) => ({
      ...u,
      checked: activeUserIds.includes(u.id) && !u.hasOwnProperty('userGroupName'),
    }));

  const getActiveGroupReviewers = (
    activeUserGroupIds: number[],
    groups: AssignedGroup[],
  ) =>
    groups.map((g) => ({
      ...g,
      checked:
        activeUserGroupIds.includes(Number(g.id)) &&
        g.hasOwnProperty('userGroupName'),
    }));

  const isClaimReviewersChanged = (
    e: ReviewersBatchItem,
    data: ClaimReviewerData,
  ) => {
    return (
      !isEqual(e.claimReviewerUserNames, data?.userReviewers) ||
      !isEqual(e.claimReviewerUserGroupIds, data?.userGroupReviewers)
    );
  };

  const isCategorySelectable = useCallback((option: IOption) => {
    const item = option.data;
    if (!item) return false;
    if ((item as MajorCategories).majorCategory) return false;
    return true;
  }, []);

  const value = claim ? (claim as any)[name] : undefined;

  //field: "Major/minor category"
  if (type === CategorySelectType.Category && !isMajor) {
    const options = selectable.data.categoriesMajorCategoriesOptions2;
    return (
      <MultiSelectExtendable
        className={classNames('category-select', {
          disabled,
        })}
        menuClassName="category-select__multiselect-menu"
        options={options}
        isSelectable={isCategorySelectable}
        itemRenderer={MajorMinorCategoryOption}
        searchEnabled
        multiple={false}
        disabled={disabled}
        hideFooter
        renderLabel={getMajMinLabelByMinorId(
          selectable.data.categoriesMajorCategories,
          value,
        )}
        searchFilter={categorySearchFilter}
        onChange={(n) => {
          if (!n.length) return;
          const item = n[0];
          editField(name, (item.data as MinorCategories).id);
        }}
      />
    );
  }

  //Matrix field: "Debtor"
  if (type === CategorySelectType.LegalEntities) {
    const options = selectable.data.legalEntities;
    return (
      <ApNiceSelect
        showValueAsTooltip
        emptyDefaultOption
        disabled={disabled}
        className={classNames('category-select__select', { disabled })}
        onChange={(e) => {
          if (disabled) return;
          editField(name, e.target.value);
        }}
        value={value}
        name={name}
        options={options.map((c) => ({
          label: formatLegalEntity(c),
          value: `${c.id}`,
        }))}
      />
    );
  }

  //field: "Resolution type"
  if (type === CategorySelectType.ActionType) {
    return (
      <ApNiceSelect
        disabled={disabled}
        className={classNames('category-select__select', { disabled })}
        onChange={(e) => editField(name, +e.target.value)}
        value={claim?.resolutionTypeId ?? 1}
        name={name}
        options={resolutionOptions.resolutionStatusOptions}
      />
    );
  }

  //field: "Resolution subtype"
  if (type === CategorySelectType.ActionSubstatus) {
    const dropDownOptions = resolutionOptions.resolutionSubstatusOptions(
      claim?.resolutionTypeId,
    );

    return (
      <ApNiceSelect
        disabled={disabled}
        className={classNames('category-select__select', { disabled })}
        onChange={(e) => editField(name, +e.target.value)}
        value={claim?.actionSubStatusId ?? 0}
        name={name}
        options={dropDownOptions}
      />
    );
  }

  //field: "Workflow status"
  if (type === CategorySelectType.ActionStatus) {
    const dropDownOptions = resolutionOptions.workflowStatusOptions(
      claim?.resolutionTypeId,
      claim?.actionSubStatusId,
    );

    const handleChange = ({ target }: ChangeEvent<HTMLSelectElement>) =>
      target.value !== EMPTY_OPTION_VALUE && editField(name, target.value);

    return (
      <ApNiceSelect
        showValueAsTooltip
        placeholder=""
        emptyDefaultOption
        disabled={disabled}
        className={classNames('category-select__select', { disabled })}
        onChange={handleChange}
        value={claim?.actionStatus ?? ''}
        name={name}
        options={dropDownOptions}
      />
    );
  }

  //field: "Resolution name"
  if (type === CategorySelectType.ActionName) {
    const options = resolutionOptions.resolutionNameOptions(
      claim?.resolutionTypeId,
      claim?.actionSubStatusId,
    );

    return (
      <ApNiceSelect
        disabled={disabled}
        className={classNames('category-select__select', { disabled })}
        onChange={(e) => editField(name, +e.target.value)}
        value={claim?.actionNameId ?? 0}
        name={name}
        options={options}
      />
    );
  }

  if (type === CategorySelectType.ClaimReviewer) {
    const { data } = activeReviewers;
    const isClaimReviewersLoading =
      allGroups.status !== FetchStatus.Done || allUsers.status !== FetchStatus.Done;

    return (
      <ReviewersEdit
        users={getActiveUserReviewers(data.userReviewers, allUsers.data || [])}
        groups={getActiveGroupReviewers(
          data.userGroupReviewers,
          allGroups.data || [],
        )}
        disabled={disabled}
        loading={isClaimReviewersLoading}
        onChange={(e) => {
          if (isClaimReviewersChanged(e, data)) {
            dispatch(setClaimReviewers(e));
            editField(name, e);
          }
        }}
        section={SectionType.Claims}
      />
    );
  }

  if (type === CategorySelectType.SurvivingClaimNo) {
    return (
      <SurvivingClaimNoSelect
        value={claim?.survivingClaimNumber}
        disabled={disabled}
        onChange={(value) => editField(name, value)}
      />
    );
  }

  if (type === CategorySelectType.ReviewLevel) {
    return (
      <ApNiceSelect
        disabled={disabled}
        className={classNames('category-select__select', { disabled })}
        onChange={(e) => editField(name, e.target.value)}
        value={value}
        name={name}
        options={ReviewLevelOptions}
      />
    );
  }

  return <div />;
};

const getMajMinLabelByMinorId = (categories: MajorCategories[], id: number) => {
  for (const maj of categories) {
    for (const min of maj.minorCategories) {
      if (min.id === id) return `${maj.majorCategory} / ${min.minorCategory}`;
    }
  }
  return '';
};

const categorySearchFilter = (o: IOption, text: string) => {
  if (o.label.toLowerCase().includes(text)) return true;
  const minorCats = (o.data as MajorCategories).minorCategories;
  if (minorCats) {
    return !!minorCats.find((c) => c.minorCategory.toLowerCase().includes(text));
  }
  return false;
};
